import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import debounce from "lodash.debounce"; 
import { showToast } from "./showToast";
import { set } from "react-hook-form";
import { use } from "react";

const useDataFetching = (fetchDataForPlatform, endPoint, title) => {
  const location = useLocation();

  const [state, setState] = useState({
    data: [],
    isError: false,
    isLoading: false,
    isRefetching: false,
    rowCount: 0,
  });

  const [columnFilters, setColumnFilters] = useState([]);

  const [globalFilter, setGlobalFilter] = useState({
    selectFillter: "",
    search: "",
  });

  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const lastFetchKey = useRef(null); 
  
  useEffect(() => {
    setGlobalFilter({ selectFillter: "", search: "" });
  }, [endPoint, title]);
  
  const fetchData = useCallback(
    async (force = false) => {
      if (state.isLoading || state.isRefetching) return;
      
      const addParamIfExists = (params, key, value) => {
        if (value) {
          params[key] = value;
        }
      };

      const params = {};

      const validPaths = ["/roleList", "/shiftlist", "/attendanceReports", "/getLockReports", "/getTrespassing", "/getfire", "/getpanicmode", "/getsalaryreport", "/userlist", "/companymasterlist", "/locationList", "/companylist", "/vendorlist", "/designationlist", "/checkpointlist", "/attendancelist"];

      const selectedFilter = validPaths.includes(location?.pathname) ? globalFilter?.selectFillter : "search";

      addParamIfExists(params, selectedFilter, globalFilter?.search);

      params.limit = pagination?.pageSize;
      params.page = pagination?.pageIndex + 1;
      if (location?.pathname === "/getTrespassing") {
        params.alert_type = "trespassing";
      } else if (location?.pathname === "/getfire") {
        params.alert_type = "fire";
      } else if (location?.pathname === "/getpanicmode") {
        params.alert_type = "panic mode";
      } else {
        delete params.alert_type;
      }

      const fetchKey = JSON.stringify({ params, endPoint, path: location.pathname });

      if (!force && lastFetchKey.current === fetchKey) return;
      lastFetchKey.current = fetchKey;

      setState((prevData) => ({
        ...prevData,
        isLoading: !prevData.data.length,
        isRefetching: prevData.data.length > 0,
      }));   

      try {
        const response = await fetchDataForPlatform(params, endPoint);
        const responseData = response?.data?.data || response?.data;
        const totalRows = response.data?.totalRecords || response?.totalRecords || 0;

        if ((!responseData.error && !responseData.status) || (responseData?.data !== undefined && responseData?.data !== null && responseData?.totalRecords !== null)) {
          setState({
            data: responseData,
            rowCount: totalRows,
            isError: false,
            isLoading: false,
            isRefetching: false,
          });
        } else {
          setState({
            data: [],
            rowCount: 0,
            isError: false,
            isLoading: false,
            isRefetching: false,
          });
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isError: true,
          isLoading: false,
          isRefetching: false,
        }));
      }
    },
    [globalFilter, pagination, fetchDataForPlatform, endPoint, location.pathname]
  );
  
  const debouncedFetchData = useMemo(() => debounce(fetchData, 500), [fetchData]);

  useEffect(() => {
    debouncedFetchData();
    return () => {
      debouncedFetchData.cancel(); 
    };
  }, [debouncedFetchData, location.pathname]);

  const manualFetchData = useCallback(() => {
    fetchData(true); 
  }, [fetchData]);

  return {
    state,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
    pagination,
    setPagination,
    manualFetchData,
  };
};

export default useDataFetching;
